import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  afterNextRender,
  inject,
} from '@angular/core';
import { TooltipOverlay } from '../tooltip/tooltip-overlay';

export type TruncateMode = 'tooltip' | 'expand';

/**
 * Clamps the host element's text and reveals the full content when it overflows.
 *
 * Usage:
 *   <span appTruncate>{{ value }}</span>                 1 line + … + hover tooltip
 *   <p appTruncate="3">{{ notes }}</p>                   clamp to 3 lines + tooltip
 *   <p appTruncate="2" truncateMode="expand">…</p>       clamp + inline "show more"
 *
 * The tooltip / toggle only appear when the text is genuinely truncated, so
 * short values render untouched. The full text is read from the element itself,
 * or from an explicit [truncateText] override.
 */
@Directive({
  selector: '[appTruncate]',
  standalone: true,
})
export class TruncateDirective implements OnDestroy {
  private lines = 1;

  /** Number of lines to clamp to. Empty attribute (`appTruncate`) means 1. */
  @Input()
  set appTruncate(value: number | string | '') {
    const parsed = typeof value === 'number' ? value : parseInt(String(value), 10);
    this.lines = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    this.applyClamp();
    this.scheduleMeasure();
  }

  /** 'tooltip' (default) reveals on hover; 'expand' shows an inline toggle. */
  @Input() truncateMode: TruncateMode = 'tooltip';

  /** Explicit full text for the tooltip; defaults to the element's textContent. */
  @Input() truncateText = '';

  /** Tooltip placement passthrough (see appTooltip). */
  @Input() tooltipPlacement: string | string[] = 'auto';

  @Input() showMoreLabel = 'Show more';
  @Input() showLessLabel = 'Show less';

  private readonly host = inject(ElementRef).nativeElement as HTMLElement;
  private readonly renderer = inject(Renderer2);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly overlay = new TooltipOverlay(this.renderer, this.host);

  private expanded = false;
  private toggleEl: HTMLElement | null = null;
  private toggleClickUnlisten: (() => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private measureRaf = 0;

  constructor() {
    afterNextRender(() => {
      this.applyClamp();
      this.measure();
      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.host);
    });
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.maybeShowTooltip();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this.maybeShowTooltip();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.overlay.hide();
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.overlay.hide();
  }

  ngOnDestroy(): void {
    this.overlay.hide();
    this.resizeObserver?.disconnect();
    this.removeToggle();
    if (this.measureRaf) {
      cancelAnimationFrame(this.measureRaf);
    }
  }

  private maybeShowTooltip(): void {
    if (this.truncateMode !== 'tooltip' || this.expanded || !this.isOverflowing()) {
      return;
    }
    const text = this.truncateText || (this.host.textContent ?? '').trim();
    this.overlay.show(text, this.tooltipPlacement);
  }

  private applyClamp(): void {
    if (this.expanded) {
      this.clearClampStyles();
      return;
    }

    if (this.lines <= 1) {
      this.renderer.removeStyle(this.host, 'display');
      this.renderer.removeStyle(this.host, '-webkit-line-clamp');
      this.renderer.removeStyle(this.host, '-webkit-box-orient');
      this.renderer.setStyle(this.host, 'overflow', 'hidden');
      this.renderer.setStyle(this.host, 'text-overflow', 'ellipsis');
      this.renderer.setStyle(this.host, 'white-space', 'nowrap');
    } else {
      this.renderer.removeStyle(this.host, 'white-space');
      this.renderer.removeStyle(this.host, 'text-overflow');
      this.renderer.setStyle(this.host, 'display', '-webkit-box');
      this.renderer.setStyle(this.host, '-webkit-box-orient', 'vertical');
      this.renderer.setStyle(this.host, '-webkit-line-clamp', String(this.lines));
      this.renderer.setStyle(this.host, 'overflow', 'hidden');
      this.renderer.setStyle(this.host, 'overflow-wrap', 'break-word');
    }
  }

  private clearClampStyles(): void {
    ['display', '-webkit-line-clamp', '-webkit-box-orient', 'overflow', 'text-overflow', 'white-space'].forEach(
      (prop) => this.renderer.removeStyle(this.host, prop),
    );
  }

  private scheduleMeasure(): void {
    if (!this.isBrowser) {
      return;
    }
    if (this.measureRaf) {
      cancelAnimationFrame(this.measureRaf);
    }
    this.measureRaf = requestAnimationFrame(() => {
      this.measureRaf = 0;
      this.measure();
    });
  }

  private measure(): void {
    if (!this.isBrowser) {
      return;
    }
    const overflowing = this.isOverflowing();

    if (this.truncateMode === 'tooltip') {
      // Pointer cursor only when the text is actually clipped, so users get a
      // visual cue that hovering reveals more.
      if (overflowing) {
        this.renderer.setStyle(this.host, 'cursor', 'pointer');
      } else {
        this.renderer.removeStyle(this.host, 'cursor');
      }
      return;
    }

    // expand mode — keep the toggle while expanded; otherwise sync to overflow.
    if (this.expanded) {
      return;
    }
    if (overflowing) {
      this.createToggle();
    } else {
      this.removeToggle();
    }
  }

  private isOverflowing(): boolean {
    if (!this.isBrowser) {
      return false;
    }
    return this.lines <= 1
      ? this.host.scrollWidth > this.host.clientWidth + 1
      : this.host.scrollHeight > this.host.clientHeight + 1;
  }

  private createToggle(): void {
    if (this.toggleEl) {
      return;
    }
    const button = this.renderer.createElement('button') as HTMLElement;
    this.renderer.setAttribute(button, 'type', 'button');
    [
      'inline-block',
      'ml-1',
      'text-xs',
      'font-medium',
      'text-indigo600',
      'hover:underline',
      'cursor-pointer',
      'align-baseline',
    ].forEach((cls) => this.renderer.addClass(button, cls));
    this.setToggleLabel(button);
    this.toggleClickUnlisten = this.renderer.listen(button, 'click', (event: Event) => {
      event.stopPropagation();
      this.toggleExpand();
    });

    const parent = this.renderer.parentNode(this.host);
    if (parent) {
      this.renderer.insertBefore(parent, button, this.renderer.nextSibling(this.host));
      this.toggleEl = button;
    }
  }

  private toggleExpand(): void {
    this.expanded = !this.expanded;
    this.applyClamp();
    if (this.toggleEl) {
      this.setToggleLabel(this.toggleEl);
    }
  }

  private setToggleLabel(button: HTMLElement): void {
    button.textContent = this.expanded ? this.showLessLabel : this.showMoreLabel;
  }

  private removeToggle(): void {
    if (this.toggleClickUnlisten) {
      this.toggleClickUnlisten();
      this.toggleClickUnlisten = null;
    }
    if (this.toggleEl) {
      const parent = this.renderer.parentNode(this.toggleEl);
      if (parent) {
        this.renderer.removeChild(parent, this.toggleEl);
      }
      this.toggleEl = null;
    }
  }
}
